import { css } from '@emotion/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import TributeFrame from '../assets/card_frame/frame_tribute.svg';
import TributeArtAllIn from '../assets/card_images/TributeArtAllIn.webp';
import TributeArtBringPeace from '../assets/card_images/TributeArtBringPeace.webp';
import TributeArtGrowBounty from '../assets/card_images/TributeArtGrowBounty.webp';
import TributeArtGrowCommunity from '../assets/card_images/TributeArtGrowCommunity.webp';
import TributeArtGrowConquest from '../assets/card_images/TributeArtGrowConquest.webp';
import TributeArtGrowFaith from '../assets/card_images/TributeArtGrowFaith.webp';
import TributeArtGrowLegend from '../assets/card_images/TributeArtGrowLegend.webp';
import TributeArtGrowPolis from '../assets/card_images/TributeArtGrowPolis.webp';
import TributeArtImpulsiveChoice from '../assets/card_images/TributeArtImpulsiveChoice.webp';
import TributeArtOverpower from '../assets/card_images/TributeArtOverpower.webp';
import TributeArtPatientChoice from '../assets/card_images/TributeArtPatientChoice.webp';
import TributeArtPersevere from '../assets/card_images/TributeArtPersevere.webp';
import TributeArtPunishWeakness from '../assets/card_images/TributeArtPunishWeakness.webp';
import TributeArtSeekHumility from '../assets/card_images/TributeArtSeekHumility.webp';
import TributeArtSeekModesty from '../assets/card_images/TributeArtSeekModesty.webp';
import TributeArtSeekWonder from '../assets/card_images/TributeArtSeekWonder.webp';
import TributeArtShowMight from '../assets/card_images/TributeArtShowMight.webp';
import TributeArtShowPiety from '../assets/card_images/TributeArtShowPiety.webp';
import layout from '../assets/tribute_card/full_layout.json';

// BaseCardView landscape aspect = 139.51 / 100 = 1.3951.
// We keep the SVG-derived rect height (slot-circle-fit dimension) and widen
// the rect to match BaseCardView's aspect.
const CARD_ASPECT = 139.51 / 100;
const CARD_RECT_STANDARD = { w: 768.5 * CARD_ASPECT, h: 768.5 };
const CARD_RECT_LARGE = { w: 1155.5 * CARD_ASPECT, h: 1155.5 };

const IMAGES: Record<string, string> = {
  AllIn: TributeArtAllIn,
  BringPeace: TributeArtBringPeace,
  GrowBounty: TributeArtGrowBounty,
  GrowCommunity: TributeArtGrowCommunity,
  GrowConquest: TributeArtGrowConquest,
  GrowFaith: TributeArtGrowFaith,
  GrowLegend: TributeArtGrowLegend,
  GrowPolis: TributeArtGrowPolis,
  ImpulsiveChoice: TributeArtImpulsiveChoice,
  Overpower: TributeArtOverpower,
  PatientChoice: TributeArtPatientChoice,
  Persevere: TributeArtPersevere,
  PunishWeakness: TributeArtPunishWeakness,
  SeekHumility: TributeArtSeekHumility,
  SeekModesty: TributeArtSeekModesty,
  SeekWonder: TributeArtSeekWonder,
  ShowMight: TributeArtShowMight,
  ShowPiety: TributeArtShowPiety,
};

type Placement = {
  key: string;
  cx: number;
  cy: number;
  rotation: number;
  size: number;
};

function slotRadius(p: Placement) {
  return p.size === 1.5 ? layout.slot_radii.large : layout.slot_radii.standard;
}

function clonePlacements(ps: readonly Placement[]): Placement[] {
  return ps.map((p) => ({ ...p }));
}

const STORAGE_KEY = 'tribute_layout_placements_v1';
const DEFAULT_CROP_SCALE = 1.0;
const DEFAULT_CROP_Y_OFFSET = 0;

type StoredLayout = {
  placements: Placement[];
  cropScale: number;
  cropYOffset: number;
};

function parsePlacementsArrayUnknown(parsed: unknown): Placement[] | null {
  if (!Array.isArray(parsed)) return null;
  const out: Placement[] = [];
  for (const item of parsed) {
    if (!item || typeof item !== 'object') return null;
    const { key, cx, cy, rotation, size } = item as Record<string, unknown>;
    if (
      typeof key !== 'string' ||
      typeof cx !== 'number' ||
      !Number.isFinite(cx) ||
      typeof cy !== 'number' ||
      !Number.isFinite(cy) ||
      typeof rotation !== 'number' ||
      !Number.isFinite(rotation) ||
      typeof size !== 'number' ||
      !Number.isFinite(size)
    ) {
      return null;
    }
    out.push({ key, cx, cy, rotation, size });
  }
  return out;
}

function pickFiniteNumber(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

// Accepts either the legacy form (top-level array of placements) or the new
// wrapper form `{ placements, cropScale, cropYOffset }`. Missing crop fields
// fall back to defaults so legacy payloads round-trip cleanly.
function parseLayoutString(raw: string): StoredLayout | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (Array.isArray(parsed)) {
    const placements = parsePlacementsArrayUnknown(parsed);
    if (!placements) return null;
    return {
      placements,
      cropScale: DEFAULT_CROP_SCALE,
      cropYOffset: DEFAULT_CROP_Y_OFFSET,
    };
  }
  if (parsed && typeof parsed === 'object') {
    const obj = parsed as Record<string, unknown>;
    const placements = parsePlacementsArrayUnknown(obj.placements);
    if (!placements) return null;
    return {
      placements,
      cropScale: pickFiniteNumber(obj.cropScale, DEFAULT_CROP_SCALE),
      cropYOffset: pickFiniteNumber(obj.cropYOffset, DEFAULT_CROP_Y_OFFSET),
    };
  }
  return null;
}

function loadStoredLayout(): StoredLayout | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw == null) return null;
    return parseLayoutString(raw);
  } catch {
    return null;
  }
}

function loadInitialStoredLayout(): StoredLayout {
  return (
    loadStoredLayout() ?? {
      placements: clonePlacements(INITIAL_PLACEMENTS),
      cropScale: DEFAULT_CROP_SCALE,
      cropYOffset: DEFAULT_CROP_Y_OFFSET,
    }
  );
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function fetchImageAsDataUri(url: string): Promise<string> {
  const resp = await fetch(url);
  if (!resp.ok) {
    throw new Error(`Failed to fetch ${url}: ${resp.status}`);
  }
  const blob = await resp.blob();
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () =>
      reject(reader.error ?? new Error('FileReader error'));
    reader.readAsDataURL(blob);
  });
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

async function buildStandaloneSvg(
  placements: readonly Placement[],
  viewBox: readonly [number, number, number, number],
): Promise<string> {
  const uniqueKeys = Array.from(new Set(placements.map((p) => p.key)));
  const dataUriByKey = new Map<string, string>();
  await Promise.all(
    uniqueKeys.map(async (key) => {
      const url = IMAGES[key];
      if (!url) return;
      dataUriByKey.set(key, await fetchImageAsDataUri(url));
    }),
  );

  const [minX, minY, w, h] = viewBox;
  const slotsXml = placements
    .map((p, i) => {
      const r = slotRadius(p);
      const href = dataUriByKey.get(p.key) ?? '';
      const clipId = `slot-clip-${i}`;
      return [
        `<g transform="translate(${p.cx} ${p.cy}) rotate(${p.rotation})">`,
        `<clipPath id="${clipId}"><circle cx="0" cy="0" r="${r}"/></clipPath>`,
        `<image x="${-r}" y="${-r}" width="${2 * r}" height="${2 * r}" preserveAspectRatio="xMidYMid slice" clip-path="url(#${clipId})" href="${escapeXml(href)}" xlink:href="${escapeXml(href)}"/>`,
        `</g>`,
      ].join('');
    })
    .join('\n');

  return [
    `<?xml version="1.0" encoding="UTF-8"?>`,
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="${minX} ${minY} ${w} ${h}">`,
    slotsXml,
    `</svg>`,
  ].join('\n');
}

// Builds an SVG mosaic mirroring the on-screen card-crops grid: top row of
// large (size=1.5) cards in a 3-col grid, then standard cards in a 4-col
// grid. Each card replicates the on-screen `CardCrop` recipe: rounded
// outer clip + black fill (the border), then the layout centered on the
// focal card, then the frame SVG overlay. Returns a single self-contained
// SVG string with every image inlined as base64.
async function buildCropsMosaicSvg(
  placements: readonly Placement[],
  cropScale: number,
  cropYOffset: number,
): Promise<string> {
  const cardAspect = 139.51 / 100;
  const totalW = 3200;
  const padding = 24;
  const gap = 12;
  const largeCols = 3;
  const standardCols = 4;

  const largeCardW = (totalW - 2 * padding - (largeCols - 1) * gap) / largeCols;
  const largeCardH = largeCardW / cardAspect;
  const standardCardW =
    (totalW - 2 * padding - (standardCols - 1) * gap) / standardCols;
  const standardCardH = standardCardW / cardAspect;

  const indexed = placements.map((p, i) => ({ p, i }));
  const larges = indexed.filter(({ p }) => p.size === 1.5);
  const standards = indexed.filter(({ p }) => p.size !== 1.5);

  const standardRows = Math.max(1, Math.ceil(standards.length / standardCols));
  const largeRowH = larges.length > 0 ? largeCardH + gap : 0;
  const totalH =
    padding +
    largeRowH +
    standardRows * standardCardH +
    (standardRows - 1) * gap +
    padding;

  // Fetch unique sketches + the frame SVG, all as base64 data URIs.
  const uniqueKeys = Array.from(new Set(placements.map((p) => p.key)));
  const dataUriByKey = new Map<string, string>();
  const [frameUri] = await Promise.all([
    fetchImageAsDataUri(TributeFrame),
    ...uniqueKeys.map(async (key) => {
      const url = IMAGES[key];
      if (!url) return;
      dataUriByKey.set(key, await fetchImageAsDataUri(url));
    }),
  ]);

  // One <g id="img-${key}"> per unique sketch in <defs>; per-slot uses become
  // tiny <use> references. Without this we'd inline every base64 JPG N×M
  // times (N placements × M cards) — easily tens of MB of SVG, which both
  // bloats the string and slows the raster step in <img>+canvas.
  const imgDefs = uniqueKeys
    .map((key) => {
      const href = dataUriByKey.get(key) ?? '';
      return `<g id="img-${key}"><image x="-1" y="-1" width="2" height="2" preserveAspectRatio="xMidYMid slice" href="${escapeXml(href)}" xlink:href="${escapeXml(href)}"/></g>`;
    })
    .join('');

  function buildOneCard(
    p: Placement,
    focalIdx: number,
    cardIdx: number,
    cardX: number,
    cardY: number,
    cardW: number,
    cardH: number,
  ): string {
    const baseRect = p.size === 1.5 ? CARD_RECT_LARGE : CARD_RECT_STANDARD;
    // Mirror on-screen `CardCrop`: scale viewBox dims by cropScale, then
    // shift its origin in Y by `rect.h * cropYOffset`.
    const rect = { w: baseRect.w * cropScale, h: baseRect.h * cropScale };
    const yShift = rect.h * cropYOffset;
    // Match on-screen `cardCard`: border = 2.51% of width, radius = 3.58%.
    const borderW = 0.0251 * cardW;
    const outerR = 0.0358 * cardW;
    const innerX = borderW;
    const innerY = borderW;
    const innerW = cardW - 2 * borderW;
    const innerH = cardH - 2 * borderW;
    const cardClipId = `card-clip-${cardIdx}`;

    const slotsXml = placements
      .map((other, j) => {
        const r = slotRadius(other);
        const desatAttrs =
          j === focalIdx ? '' : ` filter="url(#desat)" opacity="0.55"`;
        return [
          `<g transform="translate(${other.cx} ${other.cy}) rotate(${other.rotation}) scale(${r})"${desatAttrs}>`,
          `<g clip-path="url(#unit-clip)">`,
          `<use href="#img-${other.key}" xlink:href="#img-${other.key}"/>`,
          `</g>`,
          `</g>`,
        ].join('');
      })
      .join('');

    return [
      `<g transform="translate(${cardX} ${cardY})">`,
      `<clipPath id="${cardClipId}"><rect x="0" y="0" width="${cardW}" height="${cardH}" rx="${outerR}"/></clipPath>`,
      `<g clip-path="url(#${cardClipId})">`,
      `<rect x="0" y="0" width="${cardW}" height="${cardH}" fill="#000"/>`,
      `<svg x="${innerX}" y="${innerY}" width="${innerW}" height="${innerH}" viewBox="${-rect.w / 2} ${-rect.h / 2 + yShift} ${rect.w} ${rect.h}" preserveAspectRatio="xMidYMid meet">`,
      `<g transform="rotate(${-p.rotation}) translate(${-p.cx} ${-p.cy})">`,
      slotsXml,
      `</g>`,
      `</svg>`,
      `<image x="${innerX}" y="${innerY}" width="${innerW}" height="${innerH}" preserveAspectRatio="none" href="${escapeXml(frameUri)}" xlink:href="${escapeXml(frameUri)}"/>`,
      `</g>`,
      `</g>`,
    ].join('');
  }

  const cardsXml: string[] = [];
  larges.forEach(({ p, i: focalIdx }, idx) => {
    const cardX = padding + idx * (largeCardW + gap);
    const cardY = padding;
    cardsXml.push(
      buildOneCard(p, focalIdx, idx, cardX, cardY, largeCardW, largeCardH),
    );
  });
  standards.forEach(({ p, i: focalIdx }, idx) => {
    const row = Math.floor(idx / standardCols);
    const col = idx % standardCols;
    const cardX = padding + col * (standardCardW + gap);
    const cardY = padding + largeRowH + row * (standardCardH + gap);
    cardsXml.push(
      buildOneCard(
        p,
        focalIdx,
        larges.length + idx,
        cardX,
        cardY,
        standardCardW,
        standardCardH,
      ),
    );
  });

  return [
    `<?xml version="1.0" encoding="UTF-8"?>`,
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${totalW} ${totalH}">`,
    `<defs>`,
    `<clipPath id="unit-clip"><circle cx="0" cy="0" r="1"/></clipPath>`,
    `<filter id="desat" color-interpolation-filters="sRGB"><feColorMatrix type="saturate" values="0"/></filter>`,
    imgDefs,
    `</defs>`,
    `<rect width="${totalW}" height="${totalH}" fill="#1a1424"/>`,
    cardsXml.join('\n'),
    `</svg>`,
  ].join('\n');
}

function svgPointFromEvent(
  svg: SVGSVGElement,
  e: { clientX: number; clientY: number },
): { x: number; y: number } | null {
  const pt = svg.createSVGPoint();
  pt.x = e.clientX;
  pt.y = e.clientY;
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const xf = pt.matrixTransform(ctm.inverse());
  return { x: xf.x, y: xf.y };
}

type DragState = {
  idx: number;
  mode: 'translate' | 'rotate';
  startMouseSVG: { x: number; y: number };
  startCx: number;
  startCy: number;
  // Snapshot pushed to history on the first mousemove — clicks that never
  // move (incl. each half of a double-click) leave history untouched.
  startPlacements: Placement[];
};

const INITIAL_PLACEMENTS: Placement[] = (
  layout.placements as readonly Placement[]
).map((p) => ({ ...p }));

function SlotImage({
  p,
  index,
  idPrefix,
  desaturated,
}: {
  p: Placement;
  index: number;
  idPrefix: string;
  desaturated?: boolean;
}) {
  const r = slotRadius(p);
  const clipId = `${idPrefix}-slot-${index}`;
  const href = IMAGES[p.key];
  return (
    <g transform={`translate(${p.cx} ${p.cy}) rotate(${p.rotation})`}>
      <clipPath id={clipId}>
        <circle cx={0} cy={0} r={r} />
      </clipPath>
      <image
        x={-r}
        y={-r}
        width={2 * r}
        height={2 * r}
        href={href}
        clipPath={`url(#${clipId})`}
        preserveAspectRatio="xMidYMid slice"
        style={
          desaturated ? { filter: 'grayscale(1)', opacity: 0.55 } : undefined
        }
      />
    </g>
  );
}

function CardCrop({
  p,
  index,
  placements,
  cropScale,
  cropYOffset,
  highlighted,
}: {
  p: Placement;
  index: number;
  placements: readonly Placement[];
  cropScale: number;
  cropYOffset: number;
  highlighted: boolean;
}) {
  const baseRect = p.size === 1.5 ? CARD_RECT_LARGE : CARD_RECT_STANDARD;
  const rect = { w: baseRect.w * cropScale, h: baseRect.h * cropScale };
  // cropYOffset is a fraction of rect.h. Positive = focal slot appears higher
  // in the card (we shift the viewBox window down, revealing more below).
  const yShift = rect.h * cropYOffset;
  const idPrefix = `crop-${index}`;
  return (
    <div
      id={`crop-card-${index}`}
      css={[Styles.cardContainer, highlighted && Styles.cardContainerHighlight]}
    >
      <div css={Styles.cardClipped}>
        <div css={Styles.cardCard}>
          <svg
            css={Styles.cropSvg}
            viewBox={`${-rect.w / 2} ${-rect.h / 2 + yShift} ${rect.w} ${rect.h}`}
            preserveAspectRatio="xMidYMid meet"
          >
            <g
              transform={`rotate(${-p.rotation}) translate(${-p.cx} ${-p.cy})`}
            >
              {placements.map((other, i) => (
                <SlotImage
                  key={`${other.key}-${i}`}
                  p={other}
                  index={i}
                  idPrefix={idPrefix}
                  desaturated={i !== index}
                />
              ))}
            </g>
          </svg>
          <div css={Styles.cropFrame} />
        </div>
      </div>
    </div>
  );
}

function computeTightViewBox(
  placements: readonly Placement[],
): [number, number, number, number] {
  // Bounding box of all 4 corners of each rotated card-frame rect.
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const p of placements) {
    const rect = p.size === 1.5 ? CARD_RECT_LARGE : CARD_RECT_STANDARD;
    const th = (p.rotation * Math.PI) / 180;
    const cos = Math.cos(th);
    const sin = Math.sin(th);
    const hw = rect.w / 2;
    const hh = rect.h / 2;
    for (const [sx, sy] of [
      [-1, -1],
      [1, -1],
      [1, 1],
      [-1, 1],
    ]) {
      const x = sx * hw;
      const y = sy * hh;
      const tx = p.cx + cos * x - sin * y;
      const ty = p.cy + sin * x + cos * y;
      minX = Math.min(minX, tx);
      minY = Math.min(minY, ty);
      maxX = Math.max(maxX, tx);
      maxY = Math.max(maxY, ty);
    }
  }
  const pad = 50;
  return [minX - pad, minY - pad, maxX - minX + 2 * pad, maxY - minY + 2 * pad];
}

export default function TributeLayoutDebugPage() {
  // Read localStorage once on mount; the three useStates below seed off it.
  const [initialStored] = useState(loadInitialStoredLayout);
  const [placements, setPlacements] = useState<Placement[]>(
    initialStored.placements,
  );
  const [history, setHistory] = useState<Placement[][]>([]);
  const [showOutlines, setShowOutlines] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [exporting, setExporting] = useState<'png' | 'svg' | 'crops' | null>(
    null,
  );
  const [cropScale, setCropScale] = useState(initialStored.cropScale);
  const [cropYOffset, setCropYOffset] = useState(initialStored.cropYOffset);
  const [highlightedIdx, setHighlightedIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const dragHistoryPushedRef = useRef(false);

  // viewBox frozen at mount; recomputing while dragging would make the
  // whole layout shift under the cursor.
  const [viewBox] = useState<[number, number, number, number]>(() =>
    computeTightViewBox(placements),
  );
  const [minX, minY, w, h] = viewBox;
  const layoutAspect = w / h;

  // Autosave placements + crop view to localStorage so edits survive reloads.
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ placements, cropScale, cropYOffset }),
      );
    } catch {
      // quota / unavailable — ignore
    }
  }, [placements, cropScale, cropYOffset]);

  const onCopyLayout = useCallback(async () => {
    const text = JSON.stringify({ placements, cropScale, cropYOffset });
    try {
      await navigator.clipboard.writeText(text);
      window.alert(`Copied ${placements.length} placements to clipboard.`);
    } catch {
      // navigator.clipboard can fail on non-secure origins or older browsers.
      // Show a prompt so the user can still grab the JSON manually.
      window.prompt('Clipboard unavailable — copy this manually:', text);
    }
  }, [placements, cropScale, cropYOffset]);

  const onPasteLayout = useCallback(() => {
    const input = window.prompt(
      'Paste layout JSON.\n\nYour current layout will move into undo history (Ctrl/Cmd+Z restores it). If you want a hard backup, click Copy layout first.',
      '',
    );
    if (input == null) return;
    const trimmed = input.trim();
    if (!trimmed) return;
    const parsed = parseLayoutString(trimmed);
    if (!parsed) {
      window.alert('Invalid layout JSON — nothing was changed.');
      return;
    }
    if (
      !window.confirm(
        `Replace current layout with pasted (${parsed.placements.length} placements)?`,
      )
    ) {
      return;
    }
    setHistory((h) => [...h, clonePlacements(placements)]);
    setPlacements(parsed.placements);
    setCropScale(parsed.cropScale);
    setCropYOffset(parsed.cropYOffset);
  }, [placements]);

  const onJitterSmall = useCallback(() => {
    setHistory((h) => [...h, clonePlacements(placements)]);
    const stdR = layout.slot_radii.standard;
    // ±15% of the standard slot radius — small enough to nudge, not reshuffle.
    const mag = stdR * 0.15;
    setPlacements((prev) =>
      prev.map((p) =>
        p.size === 1.5
          ? p
          : {
              ...p,
              cx: p.cx + (Math.random() * 2 - 1) * mag,
              cy: p.cy + (Math.random() * 2 - 1) * mag,
            },
      ),
    );
  }, [placements]);

  const onResetToDisk = useCallback(() => {
    if (
      !window.confirm(
        'Discard local edits and reset to the disk default layout?',
      )
    ) {
      return;
    }
    setHistory((h) => [...h, clonePlacements(placements)]);
    setPlacements(clonePlacements(INITIAL_PLACEMENTS));
    setCropScale(DEFAULT_CROP_SCALE);
    setCropYOffset(DEFAULT_CROP_Y_OFFSET);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, [placements]);

  const onExportPng = useCallback(async () => {
    if (exporting) return;
    setExporting('png');
    let svgObjectUrl: string | null = null;
    try {
      const svgString = await buildStandaloneSvg(placements, viewBox);
      const svgBlob = new Blob([svgString], {
        type: 'image/svg+xml;charset=utf-8',
      });
      svgObjectUrl = URL.createObjectURL(svgBlob);
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load SVG for PNG'));
        img.src = svgObjectUrl!;
      });
      // Target a longest-side of 2400px — good enough for an on-screen
      // review or a small print. Bump if higher fidelity is needed.
      const maxSide = 2400;
      const [, , vbW, vbH] = viewBox;
      const scale = maxSide / Math.max(vbW, vbH);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(vbW * scale));
      canvas.height = Math.max(1, Math.round(vbH * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('canvas 2D context unavailable');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const pngBlob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/png'),
      );
      if (!pngBlob) throw new Error('canvas.toBlob returned null');
      downloadBlob(pngBlob, 'tribute_layout.png');
    } catch (err) {
      console.error('PNG export failed:', err);
      window.alert(`PNG export failed: ${(err as Error).message}`);
    } finally {
      if (svgObjectUrl) URL.revokeObjectURL(svgObjectUrl);
      setExporting(null);
    }
  }, [exporting, placements, viewBox]);

  const onExportSvg = useCallback(async () => {
    if (exporting) return;
    setExporting('svg');
    try {
      const svgString = await buildStandaloneSvg(placements, viewBox);
      const blob = new Blob([svgString], {
        type: 'image/svg+xml;charset=utf-8',
      });
      downloadBlob(blob, 'tribute_layout.svg');
    } catch (err) {
      console.error('SVG export failed:', err);
      window.alert(`SVG export failed: ${(err as Error).message}`);
    } finally {
      setExporting(null);
    }
  }, [exporting, placements, viewBox]);

  const onExportCrops = useCallback(async () => {
    if (exporting) return;
    setExporting('crops');
    let svgObjectUrl: string | null = null;
    try {
      const svgString = await buildCropsMosaicSvg(
        placements,
        cropScale,
        cropYOffset,
      );
      const svgBlob = new Blob([svgString], {
        type: 'image/svg+xml;charset=utf-8',
      });
      svgObjectUrl = URL.createObjectURL(svgBlob);
      // Extract the viewBox dimensions back out of the SVG header so we
      // know the canvas size. Cheaper than re-deriving the same math.
      const vbMatch = svgString.match(/viewBox="0 0 ([\d.]+) ([\d.]+)"/);
      if (!vbMatch) throw new Error('failed to read mosaic viewBox');
      const mosaicW = parseFloat(vbMatch[1]);
      const mosaicH = parseFloat(vbMatch[2]);
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () =>
          reject(new Error('Failed to load mosaic SVG for PNG'));
        img.src = svgObjectUrl!;
      });
      // mosaicW is already 3200; rasterize 1:1 for per-card detail. If
      // file size becomes painful, drop maxSide.
      const maxSide = 3200;
      const scale = maxSide / Math.max(mosaicW, mosaicH);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(mosaicW * scale));
      canvas.height = Math.max(1, Math.round(mosaicH * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('canvas 2D context unavailable');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const pngBlob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/png'),
      );
      if (!pngBlob) throw new Error('canvas.toBlob returned null');
      downloadBlob(pngBlob, 'tribute_crops.png');
    } catch (err) {
      console.error('Crops export failed:', err);
      window.alert(`Crops export failed: ${(err as Error).message}`);
    } finally {
      if (svgObjectUrl) URL.revokeObjectURL(svgObjectUrl);
      setExporting(null);
    }
  }, [exporting, placements, cropScale, cropYOffset]);

  const beginDrag = useCallback(
    (idx: number, mode: 'translate' | 'rotate', e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const svg = svgRef.current;
      if (!svg) return;
      const pt = svgPointFromEvent(svg, e);
      if (!pt) return;
      dragHistoryPushedRef.current = false;
      setDrag({
        idx,
        mode,
        startMouseSVG: pt,
        startCx: placements[idx].cx,
        startCy: placements[idx].cy,
        startPlacements: clonePlacements(placements),
      });
    },
    [placements],
  );

  useEffect(() => {
    if (!drag) return;
    const svg = svgRef.current;
    if (!svg) return;
    const onMove = (e: MouseEvent) => {
      const pt = svgPointFromEvent(svg, e);
      if (!pt) return;
      if (!dragHistoryPushedRef.current) {
        dragHistoryPushedRef.current = true;
        setHistory((h) => [...h, drag.startPlacements]);
      }
      setPlacements((prev) =>
        prev.map((p, i) => {
          if (i !== drag.idx) return p;
          if (drag.mode === 'translate') {
            return {
              ...p,
              cx: drag.startCx + (pt.x - drag.startMouseSVG.x),
              cy: drag.startCy + (pt.y - drag.startMouseSVG.y),
            };
          }
          const dx = pt.x - drag.startCx;
          const dy = pt.y - drag.startCy;
          // Local +Y-down rotated by theta CW places local (0,-1) at
          // screen (sin, -cos); solving for theta when (sin,-cos) ∝ (dx,dy)
          // gives theta = atan2(dx, -dy).
          const angle = (Math.atan2(dx, -dy) * 180) / Math.PI;
          return { ...p, rotation: angle };
        }),
      );
    };
    const onUp = () => setDrag(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag]);

  useEffect(() => {
    if (highlightedIdx === null) return;
    const t = window.setTimeout(() => setHighlightedIdx(null), 2500);
    return () => window.clearTimeout(t);
  }, [highlightedIdx]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      if (
        (e.metaKey || e.ctrlKey) &&
        (e.key === 'z' || e.key === 'Z') &&
        !e.shiftKey
      ) {
        e.preventDefault();
        setHistory((h) => {
          if (h.length === 0) return h;
          setPlacements(h[h.length - 1]);
          return h.slice(0, -1);
        });
      } else if (e.key === 'o' || e.key === 'O') {
        setShowOutlines((v) => !v);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const onSvgMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (drag) return;
    const svg = svgRef.current;
    if (!svg) return;
    const pt = svgPointFromEvent(svg, e);
    if (!pt) {
      setHoveredIdx(null);
      return;
    }
    let best: number | null = null;
    let bestDist = Infinity;
    for (let i = 0; i < placements.length; i++) {
      const p = placements[i];
      const d = Math.hypot(pt.x - p.cx, pt.y - p.cy);
      // Hover zone is generous so the handle (drawn outside the slot
      // circle) stays "in" the hovered slot.
      const r = slotRadius(p) * 1.6;
      if (d < r && d < bestDist) {
        best = i;
        bestDist = d;
      }
    }
    setHoveredIdx(best);
  };

  const onSvgMouseLeave = () => {
    if (!drag) setHoveredIdx(null);
  };

  const placementsWithIndex = placements.map((p, i) => ({ p, i }));
  const larges = placementsWithIndex.filter(({ p }) => p.size === 1.5);
  const standards = placementsWithIndex.filter(({ p }) => p.size !== 1.5);

  return (
    <div css={Styles.page}>
      <div css={Styles.toolbar}>
        <h1 css={Styles.pageTitle}>Tribute Layout Debug</h1>
        <div css={Styles.toolbarButtons}>
          <button
            css={Styles.toolbarButton}
            onClick={onCopyLayout}
            disabled={exporting !== null}
            title="Copy current layout JSON to clipboard"
          >
            Copy layout
          </button>
          <button
            css={Styles.toolbarButton}
            onClick={onPasteLayout}
            disabled={exporting !== null}
            title="Paste a layout JSON (current state moves to undo history)"
          >
            Paste layout
          </button>
          <button
            css={Styles.toolbarButton}
            onClick={onJitterSmall}
            disabled={exporting !== null}
            title="Randomly nudge each small constellation (undo with Ctrl/Cmd+Z)"
          >
            Jitter small
          </button>
          <button
            css={Styles.toolbarButton}
            onClick={onResetToDisk}
            disabled={exporting !== null}
          >
            Reset to disk
          </button>
          <button
            css={Styles.toolbarButton}
            onClick={onExportPng}
            disabled={exporting !== null}
          >
            {exporting === 'png' ? 'Exporting…' : 'Export PNG'}
          </button>
          <button
            css={Styles.toolbarButton}
            onClick={onExportSvg}
            disabled={exporting !== null}
          >
            {exporting === 'svg' ? 'Exporting…' : 'Export SVG'}
          </button>
          <button
            css={Styles.toolbarButton}
            onClick={onExportCrops}
            disabled={exporting !== null}
          >
            {exporting === 'crops' ? 'Exporting…' : 'Export crops'}
          </button>
        </div>
      </div>
      <div css={Styles.layoutSection} style={{ aspectRatio: layoutAspect }}>
        <svg
          ref={svgRef}
          css={Styles.svg}
          viewBox={`${minX} ${minY} ${w} ${h}`}
          preserveAspectRatio="xMidYMid meet"
          onMouseMove={onSvgMouseMove}
          onMouseLeave={onSvgMouseLeave}
        >
          {placements.map((p, i) => (
            <SlotImage
              key={`${p.key}-${i}`}
              p={p}
              index={i}
              idPrefix="layout"
            />
          ))}
          {showOutlines &&
            placements.map((p, i) => {
              const rect =
                p.size === 1.5 ? CARD_RECT_LARGE : CARD_RECT_STANDARD;
              return (
                <g
                  key={`outline-${p.key}-${i}`}
                  transform={`translate(${p.cx} ${p.cy}) rotate(${p.rotation})`}
                >
                  <rect
                    x={-rect.w / 2}
                    y={-rect.h / 2}
                    width={rect.w}
                    height={rect.h}
                    fill="none"
                    stroke="#c5b9c5"
                    strokeWidth={2}
                    vectorEffect="non-scaling-stroke"
                  />
                </g>
              );
            })}
          {(() => {
            // Render active slot's hit group LAST so its handle sits on top
            // of every other slot's transparent translate-circle.
            const activeIdx = drag?.idx ?? hoveredIdx;
            const order = placements.map((_, i) => i);
            if (activeIdx !== null && activeIdx !== undefined) {
              order.splice(order.indexOf(activeIdx), 1);
              order.push(activeIdx);
            }
            return order.map((i) => {
              const p = placements[i];
              const r = slotRadius(p);
              const handleLen = r * 0.75;
              const tipR = r * 0.15;
              const isActive = i === activeIdx;
              return (
                <g
                  key={`hit-${p.key}-${i}`}
                  transform={`translate(${p.cx} ${p.cy}) rotate(${p.rotation})`}
                >
                  <circle
                    r={r}
                    fill="transparent"
                    style={{ cursor: 'move' }}
                    onMouseDown={(e) => beginDrag(i, 'translate', e)}
                    onDoubleClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setHighlightedIdx(i);
                      const el = document.getElementById(`crop-card-${i}`);
                      if (el) {
                        el.scrollIntoView({
                          behavior: 'smooth',
                          block: 'center',
                        });
                      }
                    }}
                  />
                  {isActive && (
                    <g>
                      <line
                        x1={0}
                        y1={0}
                        x2={0}
                        y2={-handleLen}
                        stroke="#e8dde8"
                        strokeWidth={2}
                        vectorEffect="non-scaling-stroke"
                        pointerEvents="none"
                      />
                      <circle
                        cx={0}
                        cy={-handleLen}
                        r={tipR}
                        fill="#e8dde8"
                        fillOpacity={0.7}
                        stroke="#e8dde8"
                        strokeWidth={2}
                        vectorEffect="non-scaling-stroke"
                        style={{ cursor: 'grab' }}
                        onMouseDown={(e) => beginDrag(i, 'rotate', e)}
                      />
                    </g>
                  )}
                </g>
              );
            });
          })()}
        </svg>
      </div>
      <div css={Styles.cropsSection}>
        <div css={Styles.cropsHeader}>
          <h2 css={Styles.sectionHeader}>Card Crops</h2>
          <label css={Styles.scaleControl}>
            <span>Crop scale</span>
            <input
              type="range"
              min={0.8}
              max={1.2}
              step={0.01}
              value={cropScale}
              onChange={(e) => setCropScale(parseFloat(e.target.value))}
            />
            <span css={Styles.scaleValue}>{cropScale.toFixed(2)}×</span>
            <button
              css={Styles.toolbarButton}
              onClick={() => setCropScale(DEFAULT_CROP_SCALE)}
              disabled={cropScale === DEFAULT_CROP_SCALE}
            >
              Reset
            </button>
          </label>
          <label css={Styles.scaleControl}>
            <span>Crop Y offset</span>
            <input
              type="range"
              min={-0.1}
              max={0.1}
              step={0.005}
              value={cropYOffset}
              onChange={(e) => setCropYOffset(parseFloat(e.target.value))}
            />
            <span css={Styles.scaleValue}>
              {(cropYOffset * 100).toFixed(1)}%
            </span>
            <button
              css={Styles.toolbarButton}
              onClick={() => setCropYOffset(DEFAULT_CROP_Y_OFFSET)}
              disabled={cropYOffset === DEFAULT_CROP_Y_OFFSET}
            >
              Reset
            </button>
          </label>
        </div>
        <div css={Styles.gridSection}>
          <div css={Styles.row(3)}>
            {larges.map(({ p, i }) => (
              <CardCrop
                key={`large-${p.key}-${i}`}
                p={p}
                index={i}
                placements={placements}
                cropScale={cropScale}
                cropYOffset={cropYOffset}
                highlighted={highlightedIdx === i}
              />
            ))}
          </div>
          <div css={Styles.row(4)}>
            {standards.map(({ p, i }) => (
              <CardCrop
                key={`std-${p.key}-${i}`}
                p={p}
                index={i}
                placements={placements}
                cropScale={cropScale}
                cropYOffset={cropYOffset}
                highlighted={highlightedIdx === i}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const Styles = {
  page: css({
    minHeight: '100vh',
    background: '#000',
    color: '#c5b9c5',
    padding: 16,
    boxSizing: 'border-box',
  }),
  pageTitle: css({
    fontSize: 28,
    fontWeight: 600,
    margin: 0,
    color: '#e8dde8',
  }),
  toolbar: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 16,
  }),
  toolbarButtons: css({
    display: 'flex',
    gap: 8,
  }),
  toolbarButton: css({
    background: '#1a1424',
    color: '#c5b9c5',
    border: '1px solid #c5b9c5',
    borderRadius: 4,
    padding: '6px 12px',
    fontSize: 14,
    fontFamily: 'inherit',
    cursor: 'pointer',
    '&:hover:not(:disabled)': {
      background: '#2a2434',
      color: '#e8dde8',
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  }),
  layoutSection: css({
    width: '100%',
    maxHeight: '100vh',
    margin: '0 auto',
    // SVG sets its own intrinsic height from its viewBox aspect.
  }),
  cropsSection: css({
    marginTop: 24,
    background: '#1a1424',
    borderRadius: 8,
    padding: 16,
  }),
  sectionHeader: css({
    fontSize: 20,
    fontWeight: 600,
    margin: 0,
    color: '#e8dde8',
  }),
  cropsHeader: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
    marginBottom: 12,
  }),
  scaleControl: css({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    color: '#c5b9c5',
    '& input[type=range]': {
      width: 180,
    },
  }),
  scaleValue: css({
    fontVariantNumeric: 'tabular-nums',
    minWidth: 48,
    textAlign: 'right',
  }),
  svg: css({
    width: '100%',
    height: '100%',
    display: 'block',
  }),
  gridSection: css({
    marginTop: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  }),
  row: (cols: number) =>
    css({
      display: 'grid',
      gridTemplateColumns: `repeat(${cols}, 1fr)`,
      gap: 12,
    }),
  // Mirror BaseCardView's structure exactly:
  //  - cardContainer: aspect-locked outer box (= BaseCardView.container)
  //  - cardClipped:   rounded clip (= BaseCardView.clippedContainer)
  //  - cardCard:      black border + radius (= BaseCardView.card)
  // CARD_BORDER_WIDTH=3.5 / CARD_BORDER_RADIUS=5 / card width=139.51 →
  //   border = 3.5/139.51 = 2.51% of width = 2.51cqw
  //   radius = 5/139.51   = 3.58% of width = 3.58cqw
  cardContainer: css({
    containerType: 'inline-size',
    width: '100%',
    aspectRatio: '139.51 / 100',
    transition: 'box-shadow 200ms ease, transform 200ms ease',
    borderRadius: '3.58cqw',
  }),
  cardContainerHighlight: css({
    boxShadow: '0 0 0 4px #f4c95d, 0 0 24px 8px rgba(244, 201, 93, 0.6)',
    transform: 'scale(1.02)',
  }),
  cardClipped: css({
    position: 'relative',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    borderRadius: '3.58cqw',
  }),
  cardCard: css({
    position: 'relative',
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    border: '2.51cqw solid #000',
    borderRadius: '3.58cqw',
    background: '#000',
  }),
  cropSvg: css({
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    display: 'block',
  }),
  cropFrame: css({
    position: 'absolute',
    inset: 0,
    backgroundImage: `url(${TributeFrame})`,
    backgroundSize: '100% 100%',
    pointerEvents: 'none',
  }),
};
